import { useLocale as useLocaleR42 } from '@/lib/locale';
// AnnouncementPopup.jsx — Phase 115 promo pop-up (photo + headline + message
// + typed action button). Shared by the Pro coach website (public + preview),
// the parent portal, and the Website Builder's "Preview pop-up" button.
//
// Dismissal rules (see lib/announcements.js): once closed it stays hidden
// for the rest of the browser session, comes back after the visitor logs
// in again, or immediately when the coach changes/re-enables the pop-up.

import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRight, Phone, Sparkles } from 'lucide-react';
import {
    isAnnouncementDismissed, dismissAnnouncement, resolveAnnouncementAction,
} from '@/lib/announcements';

export function AnnouncementPopup({
    announcement,
    scope = 'site',            // 'site' | 'portal' | 'editor'
    coachKey,                  // slug or instructor id for the dismissal key
    coachName = '',
    accentColor = '#00d4ff',   // used when no theme scope is supplied
    scopeClassName = '',       // e.g. 'csite csite-portal' on the themed website
    scopeStyle,                // theme CSS vars for the portaled dialog
    actionCtx = {},            // { surface, slug, navigate, onRegister, onPrograms }
    force = false,             // editor preview: ignore dismissal, no persistence
    open: openProp,            // controlled (editor preview)
    onOpenChange,              // controlled (editor preview)
    delayMs = 900,
}) {
    const { t: t42 } = useLocaleR42();
    const controlled = typeof openProp === 'boolean';
    const [open, setOpen] = useState(false);
    const version = announcement?.version ?? 1;

    useEffect(() => {
        if (controlled) return undefined;
        if (!announcement || (!announcement.title && !announcement.message)) { setOpen(false); return undefined; }
        if (!force && isAnnouncementDismissed(scope, coachKey, version)) { setOpen(false); return undefined; }
        const t = setTimeout(() => setOpen(true), delayMs);
        return () => clearTimeout(t);
    }, [announcement, scope, coachKey, version, force, controlled, delayMs]);

    if (!announcement || (!announcement.title && !announcement.message)) return null;

    const isOpen = controlled ? openProp : open;
    const close = () => {
        if (!force) dismissAnnouncement(scope, coachKey, version);
        if (controlled) onOpenChange?.(false); else setOpen(false);
    };
    const action = resolveAnnouncementAction(announcement, actionCtx);
    const themed = !!scopeClassName;
    const btnStyle = themed ? undefined : { background: accentColor, color: '#0a1628' };
    const btnClass = themed ? 'csite-btn-primary csite-focus' : 'font-bold hover:opacity-90';

    const runAction = () => {
        if (!action) return;
        if (!force) dismissAnnouncement(scope, coachKey, version);
        if (action.onClick) {
            if (controlled) onOpenChange?.(false); else setOpen(false);
            action.onClick();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(v) => { if (!v) close(); }}>
            <DialogContent
                className={`${scopeClassName} z-[70] w-[calc(100vw-2rem)] max-w-md overflow-hidden rounded-2xl border-0 p-0 shadow-[0_30px_80px_rgba(10,22,40,0.35)] sm:w-full`}
                style={scopeStyle}
                data-testid="coach-announcement-popup"
            >
                {announcement.image_url ? (
                    <img
                        src={announcement.image_url}
                        alt={announcement.title || 'Announcement'}
                        className="aspect-[16/10] w-full object-cover"
                        data-testid="coach-announcement-image"
                    />
                ) : (
                    <div className="flex h-24 items-end px-6 pb-3" style={themed ? { background: 'var(--t-hero-wash)' } : { background: `${accentColor}22` }}>
                        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider" style={themed ? { background: 'var(--t-accent-2)', color: 'var(--t-text)' } : { background: '#fff', color: '#0a1628' }}>
                            <Sparkles className="h-3.5 w-3.5" /> {coachName ? `From ${coachName}` : t42("Announcement")}
                        </span>
                    </div>
                )}
                <div className="px-6 pb-6 pt-5" style={themed ? { color: 'var(--t-text)' } : undefined}>
                    {announcement.title ? (
                        <DialogTitle className={`${themed ? 'csite-display' : 'font-display'} text-2xl font-bold leading-tight`} data-testid="coach-announcement-title">
                            {announcement.title}
                        </DialogTitle>
                    ) : <DialogTitle className="sr-only">{t42("Announcement")}</DialogTitle>}
                    {announcement.message ? (
                        <DialogDescription className="mt-2 whitespace-pre-line text-sm leading-relaxed" style={themed ? { color: 'var(--t-muted)' } : { color: '#1a3a5c' }} data-testid="coach-announcement-message">
                            {announcement.message}
                        </DialogDescription>
                    ) : <DialogDescription className="sr-only">{t42("Special announcement from your coach")}</DialogDescription>}
                    <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
                        {action && (action.href ? (
                            <Button asChild className={`${btnClass} h-11 w-full sm:w-auto sm:flex-1`} style={btnStyle} data-testid="coach-announcement-action">
                                <a
                                    href={action.href}
                                    target={action.external ? '_blank' : undefined}
                                    rel={action.external ? 'noopener noreferrer' : undefined}
                                    onClick={() => { if (!force) dismissAnnouncement(scope, coachKey, version); }}
                                >
                                    {action.href.startsWith('tel:') ? <Phone className="mr-1.5 h-4 w-4" /> : null}
                                    {action.label}
                                    {!action.href.startsWith('tel:') ? <ArrowRight className="ml-1.5 h-4 w-4" /> : null}
                                </a>
                            </Button>
                        ) : (
                            <Button onClick={runAction} className={`${btnClass} h-11 w-full sm:w-auto sm:flex-1`} style={btnStyle} data-testid="coach-announcement-action">
                                {action.label} <ArrowRight className="ml-1.5 h-4 w-4" />
                            </Button>
                        ))}
                        <Button
                            variant="ghost"
                            onClick={close}
                            className={`h-11 w-full text-sm font-semibold sm:w-auto ${themed ? 'csite-focus' : ''}`}
                            style={themed ? { color: 'var(--t-muted)' } : { color: '#1a3a5c' }}
                            data-testid="coach-announcement-dismiss"
                        >
                            {action ? t42("No thanks") : t42("Close")}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default AnnouncementPopup;
